import { useObjectUrl, useObjectUrls } from '../../hooks/useObjectUrl';
import type { ImageDimensions } from '../../utils/imageDimensions';
import { cn } from '../../lib/utils';
import { getMediaType } from '../../utils/mediaUtils';
import { MEDIA_FADE_IN_CLASS, revealMedia } from '../Form';
import { S3Image } from '../S3Image';
import { DappPreviewShell } from '../shared/DappPreviewShell';

interface FormData {
  dappName: string;
  subtitle: string;
  description: string;
  dappIcon: File | null;
  banner: File | null;
  dappPreviews: (File | null)[];
  existingDappIconUrl?: string | null;
  existingBannerUrl?: string | null;
  existingPreviewUrls?: (string | null)[];
}

interface DappPreviewPanelWithUrlsProps {
  formData: FormData;
  maxPreviews: number;
  previewDimensions?: ImageDimensions | null;
}

export function DappPreviewPanelWithUrls({
  formData,
  maxPreviews,
  previewDimensions,
}: DappPreviewPanelWithUrlsProps) {
  const dappPreviewUrls = useObjectUrls(formData.dappPreviews);
  const iconPreviewUrl = useObjectUrl(formData.dappIcon);
  const bannerPreviewUrl = useObjectUrl(formData.banner);

  const displayIconUrl = iconPreviewUrl || formData.existingDappIconUrl;
  const displayBannerUrl = bannerPreviewUrl || formData.existingBannerUrl;
  const displayPreviewUrls = Array(maxPreviews)
    .fill(null)
    .map(
      (_, index) =>
        dappPreviewUrls[index] || formData.existingPreviewUrls?.[index] || null,
    );

  const slotMediaClass = cn(
    'h-full w-full object-contain rounded-2xl',
    MEDIA_FADE_IN_CLASS,
  );
  const slotImageClass = cn(
    'h-full w-full object-cover rounded-2xl',
    MEDIA_FADE_IN_CLASS,
  );
  const coverClass = cn('h-full w-full object-cover', MEDIA_FADE_IN_CLASS);
  const iconClass = cn(
    'h-full w-full object-cover rounded-3xl',
    MEDIA_FADE_IN_CLASS,
  );

  const previewSlots = displayPreviewUrls.map((previewUrl, index) => {
    const filePreview = formData.dappPreviews[index];
    const slotKey = `preview-slot-${index}`;

    if (!previewUrl) {
      return null;
    }

    const mediaType = getMediaType(filePreview || previewUrl);

    if (mediaType === 'video') {
      return filePreview ? (
        <video
          key={slotKey}
          src={previewUrl}
          aria-label={`Preview ${index + 1} video`}
          autoPlay
          muted
          loop
          playsInline
          onLoadedData={revealMedia}
          className={slotMediaClass}
        />
      ) : (
        <video
          key={slotKey}
          autoPlay
          muted
          loop
          playsInline
          onLoadedData={revealMedia}
          className={slotMediaClass}
        >
          <source src={previewUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }

    return filePreview ? (
      <img
        key={slotKey}
        src={previewUrl}
        alt={`Preview ${index + 1}`}
        onLoad={revealMedia}
        onError={revealMedia}
        className={slotImageClass}
      />
    ) : (
      <S3Image
        key={slotKey}
        src={previewUrl}
        alt={`Preview ${index + 1}`}
        onLoad={revealMedia}
        onError={revealMedia}
        className={slotImageClass}
      />
    );
  });

  return (
    <DappPreviewShell
      dappName={formData.dappName}
      subtitle={formData.subtitle}
      description={formData.description}
      bannerSlot={
        displayBannerUrl ? (
          formData.banner ? (
            <img
              src={displayBannerUrl}
              alt="Banner Preview"
              onLoad={revealMedia}
              onError={revealMedia}
              className={coverClass}
            />
          ) : (
            <S3Image
              src={displayBannerUrl}
              alt="Banner Preview"
              onLoad={revealMedia}
              onError={revealMedia}
              className={coverClass}
            />
          )
        ) : null
      }
      iconSlot={
        displayIconUrl ? (
          formData.dappIcon ? (
            <img
              src={displayIconUrl}
              alt="Icon Preview"
              onLoad={revealMedia}
              onError={revealMedia}
              className={iconClass}
            />
          ) : (
            <S3Image
              src={displayIconUrl}
              alt="Icon Preview"
              onLoad={revealMedia}
              onError={revealMedia}
              className={iconClass}
            />
          )
        ) : null
      }
      previewSlots={previewSlots}
      previewDimensions={previewDimensions}
    />
  );
}
